import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Database, X } from 'lucide-react';
import { SapMaterialMaster } from '@/types/domain/sap-simulator';

interface SapMaterialFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<SapMaterialMaster, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
}

export const SapMaterialFormModal: React.FC<SapMaterialFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [materialCode, setMaterialCode] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('IT Hardware');
  const [materialGroup] = useState('IT-HW');
  const [uom, setUom] = useState('EA');
  const [standardPrice, setStandardPrice] = useState(25000);
  const [plantCode, setPlantCode] = useState('PLANT-101');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!materialCode || !description) {
      setError('Material Code and Description are required.');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      await onSubmit({
        materialCode: materialCode.toUpperCase(),
        description,
        category,
        materialGroup,
        uom,
        standardPrice: Number(standardPrice),
        currency: 'INR',
        plantCode,
        status: 'ACTIVE',
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create SAP Material.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-4 animate-in fade-in">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-surface dark:bg-[#0e101f] text-foreground p-6 shadow-2xl space-y-4 font-heading relative z-50">
        <div className="flex items-center justify-between border-b border-border pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/10 text-primary">
              <Database className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-heading text-base font-extrabold text-foreground">Create SAP Material Master</h3>
              <p className="text-xs text-foreground-secondary font-medium">Add realistic reference data for PR validation lookups.</p>
            </div>
          </div>

          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0 rounded-full">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {error && (
          <div className="rounded-xl border border-rose-500/20 bg-rose-500/10 p-3 text-xs font-bold text-rose-600">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-bold text-foreground block mb-1">Material Code *</label>
              <input
                type="text"
                value={materialCode}
                onChange={(e) => setMaterialCode(e.target.value)}
                placeholder="e.g. MAT-7701"
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs font-mono text-foreground focus:outline-hidden focus:ring-2 focus:ring-primary"
                required
              />
            </div>

            <div>
              <label className="font-bold text-foreground block mb-1">Plant Code *</label>
              <select
                value={plantCode}
                onChange={(e) => setPlantCode(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs font-semibold text-foreground focus:outline-hidden focus:ring-2 focus:ring-primary"
              >
                <option value="PLANT-101">PLANT-101 (Noida R&D Hub)</option>
                <option value="PLANT-102">PLANT-102 (Bengaluru Tech Park)</option>
                <option value="PLANT-103">PLANT-103 (Mumbai Facility)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="font-bold text-foreground block mb-1">Material Description *</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. High-Performance Enterprise Server Workstation"
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs font-semibold text-foreground focus:outline-hidden focus:ring-2 focus:ring-primary"
              required
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="font-bold text-foreground block mb-1">Category</label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs font-semibold text-foreground"
              />
            </div>

            <div>
              <label className="font-bold text-foreground block mb-1">Unit of Measure (UOM)</label>
              <input
                type="text"
                value={uom}
                onChange={(e) => setUom(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs font-mono text-foreground uppercase"
              />
            </div>

            <div>
              <label className="font-bold text-foreground block mb-1">Standard Price (INR)</label>
              <input
                type="number"
                value={standardPrice}
                onChange={(e) => setStandardPrice(Number(e.target.value))}
                className="w-full rounded-xl border border-border bg-background px-3 py-2 text-xs font-semibold text-foreground"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-3 border-t border-border">
            <Button variant="outline" type="button" onClick={onClose} className="rounded-xl font-bold">
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="rounded-xl font-bold bg-primary text-white">
              {isSubmitting ? 'Saving Material...' : 'Save SAP Material'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
